import * as THREE from 'three';

// Lens magnification shader with RGB split
const lensShader = {
  uniforms: {
    tDiffuse: { value: null },
    time: { value: 0 },
    mousePos: { value: new THREE.Vector2(0.5, 0.5) },
    lensRadius: { value: 0.68 },
    lensPower: { value: .7 },
    magnification: { value: .8 },
    feather: { value: 0.5 },
    rgbOffset: { value: 0.2},
    aspect: { value: 1.0 },
    videoAspect: { value: 1.0 },    // Video aspect ratio
    scale: { value: new THREE.Vector2(1, 1) } // Scale for covering
  },
  vertexShader: `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
  fragmentShader: `
    uniform sampler2D tDiffuse;
    uniform float time;
    uniform vec2 mousePos;
    uniform float lensRadius;
    uniform float lensPower;
    uniform float magnification;
    uniform float feather;
    uniform float rgbOffset;
    uniform float aspect;
    uniform float videoAspect;
    uniform vec2 scale;
    varying vec2 vUv;

    vec2 coverScale(vec2 uv) {
      // Scale UV coordinates to maintain aspect ratio and cover
      return (uv - 0.5) * scale + 0.5;
    }

    vec2 getLensEffect(vec2 uv, vec2 center) {
      vec2 distVec = vec2((uv.x - center.x) * aspect, uv.y - center.y);
      float dist = length(distVec);
      float distPow = pow(dist / lensRadius, lensPower);
      
      float strength = 1.0 - smoothstep(0.0, 1.0, distPow);
      float mag = 1.0 + (magnification - 1.0) * strength;
      
      vec2 offset = normalize(distVec) * dist * (1.0 - 1.0/mag);
      return uv - offset * strength;
    }

    void main() {
      vec2 uv = vUv;
      vec2 mouse = mousePos;
      
      // Calculate distance for effect boundary
      vec2 distVec = vec2((uv.x - mouse.x) * aspect, uv.y - mouse.y);
      float dist = length(distVec);
      
      if (dist < lensRadius) {
        vec2 lensUv = getLensEffect(uv, mouse);
        vec2 direction = normalize(distVec);
        float splitStrength = (1.0 - dist/lensRadius) * rgbOffset;
        
        // Apply cover scaling to the sampled coordinates
        vec2 scaledUv = coverScale(lensUv);
        
        float r = texture2D(tDiffuse, scaledUv - direction * splitStrength).r;
        float g = texture2D(tDiffuse, scaledUv).g;
        float b = texture2D(tDiffuse, scaledUv + direction * splitStrength).b;
        
        vec4 lensColor = vec4(r, g, b, 1.0);
        vec4 originalColor = texture2D(tDiffuse, coverScale(uv));
        
        float edgeMix = smoothstep(lensRadius - feather, lensRadius, dist);
        gl_FragColor = mix(lensColor, originalColor, edgeMix);
      } else {
        gl_FragColor = texture2D(tDiffuse, coverScale(uv));
      }
    }
  `
};

// Scan lines shader
const scanLinesShader = {
  uniforms: {
    tDiffuse: { value: null },
    time: { value: 0 },
    scanLineIntensity: { value: 0.3 },
    scanLineCount: { value: 500.0 },
    scanLineSpeed: { value: 0.5 }
  },
  vertexShader: `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
  fragmentShader: `
    uniform sampler2D tDiffuse;
    uniform float time;
    uniform float scanLineIntensity;
    uniform float scanLineCount;
    uniform float scanLineSpeed;
    varying vec2 vUv;

    void main() {
      vec4 color = texture2D(tDiffuse, vUv);
      
      // Create scan lines effect
      float scanLine = sin(vUv.y * scanLineCount + time * scanLineSpeed);
      float scanLineEffect = 1.0 - scanLineIntensity * (1.0 - scanLine * scanLine);
      
      gl_FragColor = vec4(color.rgb * scanLineEffect, color.a);
    }
  `
};

export default class VideoCanvas {
  constructor() {
    this.init();
  }

  init() {
    
    // Setup scene
    this.element = document.querySelector('#page-welcome');
    if (!this.element) {
      return;
    }

    this.scene = new THREE.Scene();
    this.camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
    this.renderer = new THREE.WebGLRenderer();
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    
    this.element.appendChild(this.renderer.domElement);

    // Select appropriate video based on device
    this.selectVideo();
    
    // Create video texture
    this.videoTexture = new THREE.VideoTexture(this.video);
    
    // Create lens shader material
    this.lensMaterial = new THREE.ShaderMaterial({
      uniforms: {
        ...lensShader.uniforms,
        tDiffuse: { value: this.videoTexture },
        aspect: { value: window.innerWidth / window.innerHeight },
        videoAspect: { value: this.video.videoWidth / this.video.videoHeight },
        scale: { value: new THREE.Vector2(1, 1) }
      },
      vertexShader: lensShader.vertexShader,
      fragmentShader: lensShader.fragmentShader
    });

    // Create scan lines material
    this.scanLinesMaterial = new THREE.ShaderMaterial({
      uniforms: {
        ...scanLinesShader.uniforms,
        tDiffuse: { value: null },
        time: { value: 0 }
      },
      vertexShader: scanLinesShader.vertexShader,
      fragmentShader: scanLinesShader.fragmentShader
    });

    this.calculateCoverScale();

    // Create render targets for post-processing
    this.renderTarget1 = new THREE.WebGLRenderTarget(window.innerWidth, window.innerHeight);
    this.renderTarget2 = new THREE.WebGLRenderTarget(window.innerWidth, window.innerHeight);

    // Create planes for post-processing
    const geometry = new THREE.PlaneGeometry(2, 2);
    this.lensMesh = new THREE.Mesh(geometry, this.lensMaterial);
    this.scanLinesMesh = new THREE.Mesh(geometry, this.scanLinesMaterial);
    
    this.scene.add(this.lensMesh);

    // Add event listeners
    window.addEventListener('mousemove', this.onMouseMove.bind(this));
    this.renderer.domElement.addEventListener('touchstart', this.onTouchStart.bind(this));
    this.renderer.domElement.addEventListener('touchmove', this.onTouchMove.bind(this));
    this.renderer.domElement.addEventListener('touchend', this.onTouchEnd.bind(this));
    window.addEventListener('resize', this.onResize.bind(this));

    // Store previous mouse position
    this.prevMousePos = new THREE.Vector2(0.5, 0.5);
    this.isTouching = false;
    this.animate();
    setTimeout(this.onResize.bind(this), 300);
  }

  selectVideo() {
    console.log('selectVideo');
    const isMobile = window.innerWidth <= 768;
    const desktopVideo = document.getElementById('desktop-video');
    const mobileVideo = document.getElementById('mobile-video');

    if (!desktopVideo && !mobileVideo) {
      return;
    }

    // Hide both videos initially
    desktopVideo.style.display = 'none';
    mobileVideo.style.display = 'none';

    // Select and show appropriate video
    this.video = isMobile ? mobileVideo : desktopVideo;
    
    // Add load event handler
    this.video.addEventListener('loadeddata', () => {
      console.log('loadeddata');
      this.onResize();
    });
    
    this.video.play();
  }

  calculateCoverScale() {
    const containerAspect = window.innerWidth / window.innerHeight;
    const videoAspect = this.video.videoWidth / this.video.videoHeight;
    
    let scaleX = 1;
    let scaleY = 1;
    
    if (containerAspect > videoAspect) {
      // Container is wider than video
      scaleX = containerAspect / videoAspect;
    } else {
      // Container is taller than video
      scaleY = videoAspect / containerAspect;
    }
    
    this.lensMaterial.uniforms.scale.value.set(scaleX, scaleY);
  }

  onTouchStart(event) {
    event.preventDefault();
    this.isTouching = true;
    this.updateTouchPosition(event.touches[0]);
  }

  onTouchMove(event) {
    event.preventDefault();
    if (this.isTouching) {
      this.updateTouchPosition(event.touches[0]);
    }
  }

  onTouchEnd(event) {
    event.preventDefault();
    this.isTouching = false;
    // Reset to center when touch ends
    this.lensMaterial.uniforms.mousePos.value.set(0.5, 0.5);
  }

  updateTouchPosition(touch) {
    const rect = this.renderer.domElement.getBoundingClientRect();
    const x = (touch.clientX - rect.left) / rect.width;
    const y = 1.0 - ((touch.clientY - rect.top) / rect.height);
    
    if (this.lensMaterial.uniforms.mousePos) {
      this.lensMaterial.uniforms.mousePos.value.set(x, y);
    }
  }

  onMouseMove(event) {
    // Only handle mouse events if not touching
    if (this.isTouching) return;
    
    // Store previous mouse position
    this.prevMousePos.copy(this.lensMaterial.uniforms.mousePos.value);
    
    // Get mouse position relative to canvas
    const rect = this.renderer.domElement.getBoundingClientRect();
    const x = (event.clientX - rect.left) / rect.width;
    const y = 1.0 - ((event.clientY - rect.top) / rect.height);
    
    // Update mouse positions
    if (this.lensMaterial.uniforms.mousePos) {
      this.lensMaterial.uniforms.mousePos.value.set(x, y);
    }

    if (this.lensMaterial.uniforms.prevMousePos) {
      this.lensMaterial.uniforms.prevMousePos.value.copy(this.prevMousePos);
    }
  }

  onResize() {
    console.log('onResize');
    const wasMobile = window.innerWidth <= 768;
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.lensMaterial.uniforms.aspect.value = window.innerWidth / window.innerHeight;
    
    // Resize render targets
    this.renderTarget1.setSize(window.innerWidth, window.innerHeight);
    this.renderTarget2.setSize(window.innerWidth, window.innerHeight);
    
    this.calculateCoverScale();

    // Check if we need to switch videos
    const isMobile = window.innerWidth <= 768;

      this.selectVideo();
      this.videoTexture = new THREE.VideoTexture(this.video);
      this.lensMaterial.uniforms.tDiffuse.value = this.videoTexture;
      this.lensMaterial.uniforms.videoAspect.value = this.video.videoWidth / this.video.videoHeight;

  }

  animate() {
    requestAnimationFrame(this.animate.bind(this));
    
    // Update time uniforms
    this.lensMaterial.uniforms.time.value += 0.01;
    this.scanLinesMaterial.uniforms.time.value += 0.01;
    
    // First pass: render lens effect
    this.renderer.setRenderTarget(this.renderTarget1);
    this.renderer.render(this.scene, this.camera);
    
    // Second pass: apply scan lines
    this.scanLinesMaterial.uniforms.tDiffuse.value = this.renderTarget1.texture;
    this.scene.remove(this.lensMesh);
    this.scene.add(this.scanLinesMesh);
    
    this.renderer.setRenderTarget(null);
    this.renderer.render(this.scene, this.camera);
    
    // Restore scene for next frame
    this.scene.remove(this.scanLinesMesh);
    this.scene.add(this.lensMesh);
  }
}
